import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../../assets/assetKeys';
import { VISUAL_THEME } from '../../assets/visualTheme';
import { GAME_HEIGHT, GAME_WIDTH } from '../../data/gameConstants';
import { ObstacleRect } from '../../systems/ZoneCoreSystems';
import { UIStyle } from '../../ui/UIStyle';

export const ZONE1_POSITIONS = {
  SPAWN: { x: 220, y: 580 },
  SIGN: { x: 520, y: 500 },
  KAKEK: { x: 900, y: 500 },
  RAFFLESIA: { x: 750, y: 440 },
  FOREST_EXIT: { x: 1180, y: 520 },
  FOREST_BLOCK: { x: 1080, y: 520, width: 40, height: 200 },
} as const;

export class Zone1Map {
  private scene: Phaser.Scene;
  private forestBlocker?: Phaser.GameObjects.Rectangle;
  private forestLabel?: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  build(): ObstacleRect[] {
    this.drawBackground();
    this.drawPaths();
    this.drawDecorations();
    return this.buildObstacles();
  }

  openForestPath(): void {
    this.forestBlocker?.destroy();
    this.forestBlocker = undefined;
    this.forestLabel?.setColor('#52b788');
    this.forestLabel?.setText('Jalur Hutan (Terbuka)');
  }

  private drawBackground(): void {
    this.scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, TEXTURE_KEYS.BG_ZONE1)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
      .setDepth(0);

    this.scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT,
      VISUAL_THEME.zoneWarmth.zone1, 0.06,
    ).setDepth(0.5);

    const ground = this.scene.add.graphics().setDepth(1);
    ground.fillStyle(0xffffff, 0.06);
    ground.fillEllipse(250, 120, 180, 50);
    ground.fillEllipse(980, 100, 220, 56);
    ground.fillStyle(0xf2c14e, 0.06);
    ground.fillCircle(1010, 92, 46);
    ground.fillStyle(0x9ad6c1, 0.16);
    ground.fillRect(0, 336, GAME_WIDTH, 28);
    ground.fillStyle(0x5ca67d, 0.18);
    ground.fillRect(0, 392, GAME_WIDTH, 34);
    ground.fillStyle(0x4a7c59, 0.2);
    ground.fillEllipse(240, 430, 340, 84);
    ground.fillEllipse(700, 412, 520, 96);
    ground.fillEllipse(1080, 430, 360, 88);
    ground.fillStyle(0x78c97f, 0.88);
    ground.fillEllipse(GAME_WIDTH / 2, 535, 1080, 220);
    ground.fillStyle(0xffffff, 0.08);
    ground.fillEllipse(540, 566, 170, 34);
    ground.fillEllipse(820, 560, 140, 30);
  }

  private drawPaths(): void {
    const path = this.scene.add.graphics().setDepth(2);
    path.fillStyle(0xe2d3b0, 0.34);
    const points = [
      { x: 180, y: 590 },
      { x: 400, y: 560 },
      { x: ZONE1_POSITIONS.SIGN.x, y: ZONE1_POSITIONS.SIGN.y + 30 },
      { x: 680, y: 530 },
      { x: ZONE1_POSITIONS.KAKEK.x, y: ZONE1_POSITIONS.KAKEK.y + 30 },
      { x: 1020, y: 540 },
      { x: ZONE1_POSITIONS.FOREST_EXIT.x, y: ZONE1_POSITIONS.FOREST_EXIT.y },
    ];

    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];
      path.fillEllipse((a.x + b.x) / 2, (a.y + b.y) / 2, 90, 36);
    }
  }

  private drawDecorations(): void {
    const d = (x: number, y: number, key: string, scale = 1, flip = false, depth = 3) => {
      const img = this.scene.add.image(x, y, key).setScale(scale).setDepth(depth);
      if (flip) img.setFlipX(true);
      return img;
    };

    const shrubs = this.scene.add.graphics().setDepth(2.8);
    shrubs.fillStyle(0x4d8f64, 0.72);
    shrubs.fillEllipse(110, 446, 120, 34);
    shrubs.fillEllipse(245, 438, 150, 34);
    shrubs.fillEllipse(620, 430, 180, 36);
    shrubs.fillEllipse(980, 432, 220, 38);
    shrubs.fillEllipse(1170, 444, 130, 32);
    shrubs.fillStyle(0x2d6a4f, 0.42);
    shrubs.fillRect(0, 418, GAME_WIDTH, 10);
    shrubs.fillStyle(0x6d4c41, 0.42);
    shrubs.fillRect(0, 428, GAME_WIDTH, 4);

    d(120, 400, TEXTURE_KEYS.HOUSE_BUBUNGAN, 0.42);
    d(160, 460, TEXTURE_KEYS.TREE_LARGE, 0.75);
    d(320, 420, TEXTURE_KEYS.TREE_LARGE, 0.55);
    d(1050, 400, TEXTURE_KEYS.TREE_LARGE, 0.7, true);
    d(1150, 430, TEXTURE_KEYS.TREE_LARGE, 0.6, true);

    d(ZONE1_POSITIONS.SIGN.x, ZONE1_POSITIONS.SIGN.y - 10, TEXTURE_KEYS.SIGN_BOARD, 1);
    d(220, 520, TEXTURE_KEYS.KERIS_1, 0.72, false, 4).setAngle(-18).setAlpha(0.9);

    d(280, 510, TEXTURE_KEYS.RAFFLESIA, 0.45);
    d(620, 470, TEXTURE_KEYS.RAFFLESIA, 0.35);
    d(980, 480, TEXTURE_KEYS.RAFFLESIA, 0.4);
    d(90, 530, TEXTURE_KEYS.RAFFLESIA, 0.38).setAlpha(0.82);
    d(1140, 520, TEXTURE_KEYS.RAFFLESIA, 0.36).setAlpha(0.76);

    const altar = this.scene.add.graphics().setDepth(4);
    altar.fillStyle(0x6f4e37, 0.8);
    altar.fillRoundedRect(ZONE1_POSITIONS.KAKEK.x - 50, ZONE1_POSITIONS.KAKEK.y + 10, 100, 20, 4);
    altar.fillStyle(0x8b5e3c, 0.6);
    altar.fillTriangle(
      ZONE1_POSITIONS.KAKEK.x - 40, ZONE1_POSITIONS.KAKEK.y + 10,
      ZONE1_POSITIONS.KAKEK.x + 40, ZONE1_POSITIONS.KAKEK.y + 10,
      ZONE1_POSITIONS.KAKEK.x, ZONE1_POSITIONS.KAKEK.y - 20,
    );
    this.scene.add.text(210, 470, 'Warisan leluhur', {
      ...UIStyle.mapLabel(14, '#ffd966'),
    }).setOrigin(0.5).setDepth(5);

    this.forestLabel = UIStyle.addMapLabel(
      this.scene,
      ZONE1_POSITIONS.FOREST_EXIT.x,
      ZONE1_POSITIONS.FOREST_EXIT.y - 8,
      'Jalur Hutan (Terkunci)',
      15,
      '#ff6b6b',
      140,
    ).setDepth(5);
  }

  private buildObstacles(): ObstacleRect[] {
    const obs: ObstacleRect[] = [
      { x: GAME_WIDTH / 2, y: 165, width: GAME_WIDTH, height: 330 },
      { x: GAME_WIDTH / 2, y: GAME_HEIGHT - 20, width: GAME_WIDTH, height: 40 },
      { x: 30, y: GAME_HEIGHT / 2, width: 60, height: GAME_HEIGHT },
      { x: GAME_WIDTH - 20, y: GAME_HEIGHT / 2, width: 40, height: GAME_HEIGHT },
      { x: 170, y: 390, width: 320, height: 86 },
      { x: 680, y: 382, width: 460, height: 82 },
      { x: 1085, y: 392, width: 290, height: 86 },
      { x: 160, y: 430, width: 60, height: 80 },
      { x: 320, y: 400, width: 50, height: 70 },
      { x: 1050, y: 410, width: 55, height: 75 },
      { x: 120, y: 420, width: 100, height: 60 },
      { x: ZONE1_POSITIONS.SIGN.x, y: ZONE1_POSITIONS.SIGN.y + 20, width: 40, height: 30 },
      { x: ZONE1_POSITIONS.KAKEK.x, y: ZONE1_POSITIONS.KAKEK.y + 25, width: 70, height: 25 },
    ];

    const fb = ZONE1_POSITIONS.FOREST_BLOCK;
    this.forestBlocker = this.scene.add.rectangle(fb.x, fb.y, fb.width, fb.height, 0xc1121f, 0.35);
    this.forestBlocker.setDepth(6);
    obs.push({ x: fb.x, y: fb.y, width: fb.width, height: fb.height });

    return obs;
  }
}
